"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import NameSearchFilter from "../../../../components/name-search-filter.jsx";
import Button from "../../../../components/Button.jsx";
import Table from "../../../../components/Table.jsx";
import Pagination from "../../../../components/Pagination.jsx";
import styles from "../css/ConsultationHistory.module.css";

import { listConsultationHistoryPatients } from "../../../../api/clinic/consultationApi.js";

export default function ConsultationHistory() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await listConsultationHistoryPatients();
        if (!mounted) return;
        setPatients(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || "Failed to load patients");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false };
  }, []);

  const rows = useMemo(() => {
    return (patients || []).map((p) => {
      const first = (p.firstname || "").trim();
      const last = (p.surname || "").trim();
      const patientName = `${first} ${last}`.trim();
      const initial = first ? first[0] : "";
      return {
        id: p.patientid,
        surname: last || patientName || "",
        initial,
        title: p.title || "",
        address: p.homeaddress || "",
        patientName,
      };
    });
  }, [patients]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.surname, r.initial, r.title, r.address, r.patientName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  const pageSize = 4;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const start = (page - 1) * pageSize;
  const current = filtered.slice(start, start + pageSize);

  const columns = [
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Address", accessor: "address" },
    {
      header: "Action",
      accessor: "id",
      render: (_val, row) => (
        <Button variant="primary" onClick={() => navigate(`/customer/clinic/consultation-history/${row.id}/services-done`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.container}>
        <h2 className={styles.pageTitle}>Consultation History</h2>

        <div className={styles.filterRow}>
          <NameSearchFilter
            placeholder="Search Name"
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <section className={styles.tableArea}>
          {error && (
            <div className={styles.inlineError}>{error}</div>
          )}
          {loading ? (
            <div className={styles.loadingMessage}>Loading...</div>
          ) : (
            <Table columns={columns} data={current} />
          )}
          <div className={styles.paginationRow}>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
